import type { CalendarEvent } from 'shared-types';

type Params = {
  event: Pick<
    CalendarEvent,
    'endDate' | 'feedbackAccessEndDate' | 'feedbackAccessStartDate'
  >;
  now: number;
};

/**
 * Tells whether an event's feedback form is open for submissions right now.
 * There is nothing to give feedback on until the event is over, so feedback
 * opens when it ends unless an explicit start date says otherwise, and it
 * stays open unless an explicit end date has passed.
 *
 * Both the routes that gate on the window and the views that explain it to a
 * user go through this, so the fallback to `endDate` is defined in one place.
 * Pass `now` in rather than reading the clock here, so that a component can
 * freeze it for a render and a route can share one value across its checks.
 */
const getCalendarEventFeedbackWindowState = ({
  event,
  now
}: Params): 'closed' | 'not_open_yet' | 'open' => {
  const opensAt = event.feedbackAccessStartDate || event.endDate;
  if (opensAt && now < new Date(opensAt).getTime()) return 'not_open_yet';

  const closesAt = event.feedbackAccessEndDate;
  if (closesAt && now > new Date(closesAt).getTime()) return 'closed';

  return 'open';
};

export default getCalendarEventFeedbackWindowState;
